import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  DollarSign,
  Plus,
  Edit,
  Trash2,
  Filter,
  Search,
  FileText,
  TrendingUp,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  X,
} from "lucide-react";
import {
  Expense,
  CreateExpenseData,
  UpdateExpenseData,
  ExpenseFilters,
  ExpenseCategory,
  ApprovalStatus,
} from "../../types/expense";
import ExpenseService from "../../services/expenses";
import ExpenseForm from "../../components/forms/ExpenseForm";
import { useAuth } from "../../hooks/useAuth";
import { useHorizontalDragScroll } from "../../hooks/useHorizontalDragScroll";
import Pagination from "../../components/common/Pagination";

const AdminExpenses: React.FC = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const tableScrollRef = useHorizontalDragScroll<HTMLDivElement>();
  const [searchTerm, setSearchTerm] = useState("");
  const [activeSearchTerm, setActiveSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [dateFilter, setDateFilter] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Handle Enter key press to trigger search
  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setActiveSearchTerm(searchTerm);
      setCurrentPage(1);
    }
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  // Modal states
  const [isExpenseFormOpen, setIsExpenseFormOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);

  // Build filters object
  const filters: ExpenseFilters = {
    page: currentPage,
    limit: pageSize,
    search: activeSearchTerm || undefined,
    category:
      categoryFilter !== "all"
        ? (categoryFilter as ExpenseCategory)
        : undefined,
    approvalStatus:
      statusFilter !== "all" ? (statusFilter as ApprovalStatus) : undefined,
    // Handle date filtering with startDate/endDate based on dateFilter value
    ...(dateFilter !== "all" && {
      startDate: getDateFilterStart(dateFilter),
      endDate: getDateFilterEnd(dateFilter),
    }),
  };

  // Helper functions for date filtering
  function getDateFilterStart(filter: string): string {
    const now = new Date();
    switch (filter) {
      case "thisMonth":
        return new Date(now.getFullYear(), now.getMonth(), 1)
          .toISOString()
          .split("T")[0];
      case "lastMonth":
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        return lastMonth.toISOString().split("T")[0];
      case "thisYear":
        return new Date(now.getFullYear(), 0, 1).toISOString().split("T")[0];
      default:
        return new Date().toISOString().split("T")[0];
    }
  }

  function getDateFilterEnd(filter: string): string {
    const now = new Date();
    switch (filter) {
      case "thisMonth":
        return new Date(now.getFullYear(), now.getMonth() + 1, 0)
          .toISOString()
          .split("T")[0];
      case "lastMonth":
        const lastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
        return lastMonth.toISOString().split("T")[0];
      case "thisYear":
        return new Date(now.getFullYear(), 11, 31).toISOString().split("T")[0];
      default:
        return new Date().toISOString().split("T")[0];
    }
  }

  // Queries
  const {
    data: expensesData,
    isLoading: expensesLoading,
    error: expensesError,
  } = useQuery({
    queryKey: ["expenses", filters],
    queryFn: () => ExpenseService.getExpenses(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });

  // Get expense categories for filter
  const [shouldFetchCategories, setShouldFetchCategories] = useState(false);
  const { data: categoriesData } = useQuery({
    queryKey: ["expenseCategories"],
    queryFn: () => ExpenseService.getExpenseCategories(),
    staleTime: 10 * 60 * 1000, // 10 minutes
    enabled: shouldFetchCategories,
    refetchOnWindowFocus: false,
    onError: (error) => {
      console.error("Failed to fetch expense categories:", error);
    },
    onSuccess: (data) => {
      console.log("Expense categories data:", data);
    },
  });

  // Get expense summary for statistics
  const { data: summaryData } = useQuery({
    queryKey: ["expenseSummary"],
    queryFn: () => ExpenseService.getExpenseSummary(),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Calculate statistics
  const statistics = React.useMemo(() => {
    if (!summaryData?.data) {
      return {
        totalExpenses: 0,
        monthlyTotal: 0,
        pendingApproval: 0,
        approvedExpenses: 0,
      };
    }

    const data = summaryData.data;
    return {
      totalExpenses: data.totalExpenses || 0,
      monthlyTotal: data.monthlyTotal || 0,
      pendingApproval: data.pendingApproval || 0,
      approvedExpenses: data.approvedExpenses || 0,
    };
  }, [summaryData]);

  // Mutations
  const createMutation = useMutation({
    mutationFn: ExpenseService.createExpense,
    onSuccess: () => {
      // Invalidate all related queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ["expenses"] });
      queryClient.invalidateQueries({ queryKey: ["expenseSummary"] });
      // Force refetch to immediately show the new expense
      queryClient.refetchQueries({ queryKey: ["expenses", filters] });
      queryClient.refetchQueries({ queryKey: ["expenseSummary"] });
      toast.success("Expense created successfully");
      setIsExpenseFormOpen(false);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Failed to create expense");
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateExpenseData }) =>
      ExpenseService.updateExpense(id, data),
    onSuccess: () => {
      // Invalidate all related queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ["expenses"] });
      queryClient.invalidateQueries({ queryKey: ["expenseSummary"] });
      // Force refetch to immediately show the updated expense
      queryClient.refetchQueries({ queryKey: ["expenses", filters] });
      queryClient.refetchQueries({ queryKey: ["expenseSummary"] });
      toast.success("Expense updated successfully");
      setIsExpenseFormOpen(false);
      setEditingExpense(null);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Failed to update expense");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: ExpenseService.deleteExpense,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expenses"] });
      queryClient.invalidateQueries({ queryKey: ["expenseSummary"] });
      toast.success("Expense deleted successfully");
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Failed to delete expense");
    },
  });

  const approveMutation = useMutation({
    mutationFn: ({
      id,
      status,
      notes,
    }: {
      id: string;
      status: ApprovalStatus;
      notes: string;
    }) =>
      ExpenseService.updateApprovalStatus(id, {
        approvalStatus: status,
        approvalNotes: notes,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expenses"] });
      queryClient.invalidateQueries({ queryKey: ["expenseSummary"] });
      toast.success("Expense approval updated successfully");
      setIsApprovalModalOpen(false);
      setSelectedExpense(null);
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message || "Failed to update expense approval",
      );
    },
  });

  // Handlers
  const handleCreateExpense = (data: CreateExpenseData) => {
    createMutation.mutate(data);
  };

  const handleEditExpense = (expense: Expense) => {
    if (expense.approvalStatus === "approved") {
      toast.error("Cannot edit approved expense");
      return;
    }

    // Check if user can edit this expense
    if (
      user?.roles?.includes("branchadmin") &&
      expense.recordedBy?._id !== user?.id
    ) {
      toast.error("You can only edit expenses you created");
      return;
    }

    setEditingExpense(expense);
    setIsExpenseFormOpen(true);
  };

  const handleUpdateExpense = (data: UpdateExpenseData) => {
    if (editingExpense) {
      updateMutation.mutate({ id: editingExpense._id, data });
    }
  };

  const handleSubmitExpense = (data: CreateExpenseData | UpdateExpenseData) => {
    if (editingExpense) {
      handleUpdateExpense(data as UpdateExpenseData);
    } else {
      handleCreateExpense(data as CreateExpenseData);
    }
  };

  const handleDeleteExpense = (id: string) => {
    const expense = expensesData?.data?.find((e) => e._id === id);
    if (expense?.approvalStatus === "approved") {
      toast.error("Cannot delete approved expense");
      return;
    }
    if (confirm("Are you sure you want to delete this expense?")) {
      deleteMutation.mutate(id);
    }
  };

  const handleViewExpense = (expense: Expense) => {
    // Check if user can view this expense
    if (
      (user?.roles?.includes("branchadmin") ||
        user?.roles?.includes("secretary")) &&
      expense.recordedBy?._id !== user?.id
    ) {
      toast.error("You can only view expenses you created");
      return;
    }

    setSelectedExpense(expense);
    setIsViewModalOpen(true);
  };

  const handleApproveExpense = (status: ApprovalStatus, notes: string) => {
    if (selectedExpense) {
      approveMutation.mutate({
        id: selectedExpense._id,
        status,
        notes,
      });
    }
  };

  const handleOpenApprovalModal = (expense: Expense) => {
    setSelectedExpense(expense);
    setIsApprovalModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsExpenseFormOpen(false);
    setEditingExpense(null);
  };

  const handleCloseViewModal = () => {
    setIsViewModalOpen(false);
    setSelectedExpense(null);
  };

  const resetFilters = () => {
    setSearchTerm("");
    setCategoryFilter("all");
    setStatusFilter("all");
    setDateFilter("all");
    setCurrentPage(1);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "approved":
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case "rejected":
        return <XCircle className="w-4 h-4 text-red-500" />;
      case "pending":
        return <Clock className="w-4 h-4 text-yellow-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "approved":
        return "bg-gradient-to-r from-emerald-400 to-emerald-500 text-white shadow-sm";
      case "rejected":
        return "bg-gradient-to-r from-red-400 to-pink-500 text-white shadow-sm";
      case "pending":
        return "bg-gradient-to-r from-yellow-400 to-yellow-500 text-white shadow-sm";
      default:
        return "bg-gradient-to-r from-slate-300 to-slate-400 text-white shadow-sm";
    }
  };

  if (expensesError) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Error Loading Expenses
          </h3>
          <p className="text-gray-600">
            {(expensesError as Error)?.message || "Failed to load expenses"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 lg:p-6 space-y-4 lg:space-y-6 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 min-h-screen">
      {/* Header */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-lg p-4 lg:p-6 border border-slate-200/60">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900">
              Expense Management
            </h1>
            <p className="text-slate-600 text-sm sm:text-base font-medium">
              Track and manage branch expenses
            </p>
          </div>
          <button
            onClick={() => setIsExpenseFormOpen(true)}
            className="flex items-center px-3 sm:px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 font-semibold"
          >
            <Plus className="w-5 h-5 mr-2" />
            <span className="hidden sm:inline">Add Expense</span>
            <span className="sm:hidden">Add</span>
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      {!user?.roles?.includes("secretary") && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
          <div className="bg-white/95 backdrop-blur-xl p-4 lg:p-6 rounded-2xl border border-blue-200/60 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-blue-600">
                  Total Expenses
                </p>
                <p className="text-xl sm:text-2xl font-bold text-blue-900">
                  KSh {statistics.totalExpenses.toLocaleString()}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-300 to-blue-400 flex items-center justify-center shadow-md">
                <DollarSign className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white/95 backdrop-blur-xl p-4 lg:p-6 rounded-2xl border border-emerald-200/60 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-emerald-600">
                  This Month
                </p>
                <p className="text-xl sm:text-2xl font-bold text-emerald-900">
                  KSh {statistics.monthlyTotal.toLocaleString()}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-300 to-emerald-400 flex items-center justify-center shadow-md">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white/95 backdrop-blur-xl p-4 lg:p-6 rounded-2xl border border-yellow-200/60 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-amber-600">
                  Pending Approval
                </p>
                <p className="text-xl sm:text-2xl font-bold text-amber-900">
                  {statistics.pendingApproval}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-300 to-yellow-400 flex items-center justify-center shadow-md">
                <Clock className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white/95 backdrop-blur-xl p-4 lg:p-6 rounded-2xl border border-emerald-200/60 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-emerald-600">
                  Approved
                </p>
                <p className="text-xl sm:text-2xl font-bold text-emerald-900">
                  {statistics.approvedExpenses}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-300 to-emerald-400 flex items-center justify-center shadow-md">
                <CheckCircle className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white/95 backdrop-blur-xl p-4 lg:p-6 rounded-2xl shadow-lg border border-slate-200/60">
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
          <div className="flex flex-col sm:flex-row gap-4 flex-1 w-full">
            <div className="relative min-w-0 flex-1">
              <input
                type="text"
                placeholder="Search expenses by title or vendor... (Press Enter)"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                className="pl-10 pr-4 py-2 w-full border border-slate-300/60 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm transition-all duration-300 font-medium"
              />
            </div>

            <select
              value={categoryFilter}
              onChange={(e) => {
                setCategoryFilter(e.target.value);
                setCurrentPage(1);
              }}
              onFocus={() => setShouldFetchCategories(true)}
              className="px-4 py-2 border border-slate-300/60 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm transition-all duration-300 font-semibold text-slate-700"
            >
              <option value="all">All Categories</option>
              {categoriesData?.data && Array.isArray(categoriesData.data)
                ? categoriesData.data.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))
                : // Fallback to hardcoded categories if API fails
                  [
                    "Salaries",
                    "Utilities",
                    "Maintenance",
                    "Supplies",
                    "Transport",
                    "Marketing",
                    "Insurance",
                    "Equipment",
                    "Food & Catering",
                    "Professional Services",
                    "Rent",
                    "Communication",
                    "Training",
                    "Technology",
                    "Security",
                    "Cleaning",
                    "Medical",
                    "Miscellaneous",
                  ].map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
            </select>

            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="px-4 py-2 border border-slate-300/60 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm transition-all duration-300 font-semibold text-slate-700"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>

            <select
              value={dateFilter}
              onChange={(e) => {
                setDateFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="px-4 py-2 border border-slate-300/60 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm transition-all duration-300 font-semibold text-slate-700"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="this_week">This Week</option>
              <option value="this_month">This Month</option>
              <option value="last_month">Last Month</option>
              <option value="this_quarter">This Quarter</option>
            </select>
          </div>

          <button
            onClick={resetFilters}
            className="flex items-center px-4 py-2 bg-gradient-to-r from-slate-500 to-slate-600 hover:from-slate-600 hover:to-slate-700 text-white rounded-xl transition-all duration-300 shadow-md hover:shadow-lg hover:scale-105 font-semibold whitespace-nowrap"
          >
            <Filter className="w-4 h-4 mr-2" />
            Reset Filters
          </button>
        </div>
      </div>

      {/* Expenses Table */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-lg overflow-hidden border border-slate-200/60">
        {expensesLoading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-slate-200 border-t-blue-500 mx-auto"></div>
            <p className="mt-4 text-slate-600 font-medium">
              Loading expenses...
            </p>
          </div>
        ) : expensesData?.data?.length === 0 ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-300 to-indigo-400 flex items-center justify-center mx-auto mb-4 shadow-md">
              <FileText className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">
              No Expenses Found
            </h3>
            <p className="text-slate-600 font-medium mb-4">
              Get started by recording your first expense.
            </p>
            <button
              onClick={() => setIsExpenseFormOpen(true)}
              className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 font-semibold"
            >
              Add Expense
            </button>
          </div>
        ) : (
          <div ref={tableScrollRef} className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-gradient-to-r from-slate-50 to-blue-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white/50 divide-y divide-slate-200">
                {expensesData?.data
                  ?.filter((expense) => {
                    // Filter out expenses that secretaries and branch admins didn't create
                    if (
                      user?.roles?.includes("branchadmin") ||
                      user?.roles?.includes("secretary")
                    ) {
                      return expense.recordedBy?._id === user.id;
                    }
                    // Other roles can see all expenses
                    return true;
                  })
                  .map((expense) => (
                    <tr
                      key={expense._id}
                      className="hover:bg-blue-50/50 transition-colors duration-200"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                            {expense.description}
                            {expense.approvalStatus === "approved" && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-bold bg-gradient-to-r from-emerald-400 to-emerald-500 text-white shadow-sm">
                                ✓ Approved
                              </span>
                            )}
                          </div>
                          {expense.vendor?.name && (
                            <div className="text-sm text-slate-600 font-medium">
                              Vendor: {expense.vendor.name}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-blue-300 to-indigo-400 text-white shadow-sm">
                          {expense.category}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-slate-900">
                        KSh {expense.amount.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 font-medium">
                        {new Date(expense.date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold ${getStatusColor(
                            expense.approvalStatus,
                          )}`}
                        >
                          {getStatusIcon(expense.approvalStatus)}
                          {expense.approvalStatus}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleViewExpense(expense)}
                            disabled={
                              (user?.roles.includes("branchadmin") ||
                                user?.roles.includes("secretary")) &&
                              expense.recordedBy._id !== user.id
                            }
                            className={`p-2 rounded-lg transition-all duration-300 ${
                              (user?.roles.includes("branchadmin") ||
                                user?.roles.includes("secretary")) &&
                              expense.recordedBy._id !== user.id
                                ? "text-slate-400 cursor-not-allowed"
                                : "text-blue-600 hover:bg-blue-50 hover:scale-110"
                            }`}
                            title={
                              (user?.roles.includes("branchadmin") ||
                                user?.roles.includes("secretary")) &&
                              expense.recordedBy._id !== user.id
                                ? "Can only view expenses you created"
                                : "View expense"
                            }
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {expense.approvalStatus === "pending" &&
                            !user?.roles.includes("secretary") && (
                              <button
                                onClick={() => handleOpenApprovalModal(expense)}
                                className="p-2 rounded-lg text-amber-600 hover:bg-amber-50 hover:scale-110 transition-all duration-300"
                                title="Approve/Reject"
                              >
                                <Clock className="w-4 h-4" />
                              </button>
                            )}
                          <button
                            onClick={() => handleEditExpense(expense)}
                            disabled={
                              expense.approvalStatus === "approved" ||
                              (user?.roles.includes("branchadmin") &&
                                expense.recordedBy._id !== user.id)
                            }
                            className={`p-2 rounded-lg transition-all duration-300 ${
                              expense.approvalStatus === "approved" ||
                              (user?.roles.includes("branchadmin") &&
                                expense.recordedBy._id !== user.id)
                                ? "text-slate-400 cursor-not-allowed"
                                : "text-indigo-600 hover:bg-indigo-50 hover:scale-110"
                            }`}
                            title={
                              expense.approvalStatus === "approved"
                                ? "Cannot edit approved expense"
                                : user?.roles.includes("branchadmin") &&
                                    expense.recordedBy._id !== user.id
                                  ? "Can only edit expenses you created"
                                  : "Edit expense"
                            }
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteExpense(expense._id)}
                            disabled={expense.approvalStatus === "approved"}
                            className={`p-2 rounded-lg transition-all duration-300 ${
                              expense.approvalStatus === "approved"
                                ? "text-slate-400 cursor-not-allowed"
                                : "text-red-600 hover:bg-red-50 hover:scale-110"
                            }`}
                            title={
                              expense.approvalStatus === "approved"
                                ? "Cannot delete approved expense"
                                : "Delete expense"
                            }
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {expensesData && expensesData.pagination && (
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
          <Pagination
            currentPage={currentPage}
            totalPages={expensesData.pagination.totalPages}
            onPageChange={setCurrentPage}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>
      )}

      {/* Expense Form Modal */}
      {isExpenseFormOpen && (
        <ExpenseForm
          expense={editingExpense}
          onSubmit={handleSubmitExpense}
          onCancel={handleCloseModal}
          isLoading={createMutation.isPending || updateMutation.isPending}
        />
      )}

      {/* View Expense Modal */}
      {isViewModalOpen && selectedExpense && (
        <div className="fixed inset-0 bg-slate-900/75 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-slate-200/60">
            <div className="flex items-center justify-between p-6 border-b border-slate-200/60 bg-gradient-to-r from-slate-50 to-blue-50">
              <h2 className="text-xl font-bold text-slate-900">
                Expense Details
              </h2>
              <button
                onClick={handleCloseViewModal}
                className="text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200/60">
                  <label className="block text-sm font-bold text-blue-600 mb-1">
                    Description
                  </label>
                  <p className="text-sm font-semibold text-slate-900">
                    {selectedExpense.description}
                  </p>
                </div>
                <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200/60">
                  <label className="block text-sm font-bold text-blue-600 mb-1">
                    Category
                  </label>
                  <p className="text-sm font-semibold text-slate-900">
                    {selectedExpense.category}
                  </p>
                </div>
                <div className="p-4 bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl border border-emerald-200/60">
                  <label className="block text-sm font-bold text-emerald-600 mb-1">
                    Amount
                  </label>
                  <p className="text-sm font-bold text-emerald-900">
                    KSh {selectedExpense.amount.toLocaleString()}
                  </p>
                </div>
                <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200/60">
                  <label className="block text-sm font-bold text-blue-600 mb-1">
                    Date
                  </label>
                  <p className="text-sm font-semibold text-slate-900">
                    {new Date(selectedExpense.date).toLocaleDateString()}
                  </p>
                </div>
                <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200/60">
                  <label className="block text-sm font-bold text-blue-600 mb-1">
                    Payment Method
                  </label>
                  <p className="text-sm font-semibold text-slate-900">
                    {selectedExpense.paymentMethod}
                  </p>
                </div>
                <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200/60">
                  <label className="block text-sm font-bold text-blue-600 mb-1">
                    Status
                  </label>
                  <span
                    className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold ${getStatusColor(
                      selectedExpense.approvalStatus,
                    )}`}
                  >
                    {getStatusIcon(selectedExpense.approvalStatus)}
                    {selectedExpense.approvalStatus}
                  </span>
                </div>
              </div>
              {selectedExpense.vendor?.name && (
                <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl border border-purple-200/60">
                  <label className="block text-sm font-bold text-purple-600 mb-1">
                    Vendor
                  </label>
                  <p className="text-sm font-semibold text-slate-900">
                    {selectedExpense.vendor.name}
                  </p>
                </div>
              )}
              {selectedExpense.notes && (
                <div className="p-4 bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl border border-amber-200/60">
                  <label className="block text-sm font-bold text-amber-600 mb-1">
                    Notes
                  </label>
                  <p className="text-sm font-medium text-slate-900">
                    {selectedExpense.notes}
                  </p>
                </div>
              )}
              {selectedExpense.tags.length > 0 && (
                <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200/60">
                  <label className="block text-sm font-bold text-blue-600 mb-2">
                    Tags
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {selectedExpense.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-slate-300 to-slate-400 text-white shadow-sm"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-4 p-6 border-t border-slate-200/60">
              <button
                onClick={handleCloseViewModal}
                className="px-4 py-2 bg-gradient-to-r from-slate-500 to-slate-600 hover:from-slate-600 hover:to-slate-700 text-white rounded-xl transition-all duration-300 shadow-md hover:shadow-lg hover:scale-105 font-semibold"
              >
                Close
              </button>
              <button
                onClick={() => {
                  handleCloseViewModal();
                  handleEditExpense(selectedExpense);
                }}
                disabled={
                  selectedExpense.approvalStatus === "approved" ||
                  (user?.roles.includes("branchadmin") &&
                    selectedExpense.recordedBy._id !== user.id)
                }
                className={`flex items-center px-4 py-2 rounded-xl transition-all duration-300 shadow-md font-semibold ${
                  selectedExpense.approvalStatus === "approved" ||
                  (user?.roles.includes("branchadmin") &&
                    selectedExpense.recordedBy._id !== user.id)
                    ? "bg-slate-300 text-slate-500 cursor-not-allowed"
                    : "bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 text-white hover:shadow-lg hover:scale-105"
                }`}
                title={
                  selectedExpense.approvalStatus === "approved"
                    ? "Cannot edit approved expense"
                    : user?.roles.includes("branchadmin") &&
                        selectedExpense.recordedBy._id !== user.id
                      ? "Can only edit expenses you created"
                      : "Edit expense"
                }
              >
                <Edit className="w-4 h-4 mr-2" />
                {selectedExpense.approvalStatus === "approved"
                  ? "Expense Approved"
                  : user?.roles.includes("branchadmin") &&
                      selectedExpense.recordedBy._id !== user.id
                    ? "Cannot Edit"
                    : "Edit Expense"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Approval Modal */}
      {isApprovalModalOpen && selectedExpense && (
        <ApprovalModal
          expense={selectedExpense}
          onApprove={(notes: string) => handleApproveExpense("approved", notes)}
          onReject={(notes: string) => handleApproveExpense("rejected", notes)}
          onHold={(notes: string) => handleApproveExpense("on_hold", notes)}
          onClose={() => {
            setIsApprovalModalOpen(false);
            setSelectedExpense(null);
          }}
        />
      )}
    </div>
  );
};

// Approval Modal Component
interface ApprovalModalProps {
  expense: Expense;
  onApprove: (notes: string) => void;
  onReject: (notes: string) => void;
  onHold: (notes: string) => void;
  onClose: () => void;
}

const ApprovalModal: React.FC<ApprovalModalProps> = ({
  expense,
  onApprove,
  onReject,
  onHold,
  onClose,
}) => {
  const [notes, setNotes] = useState("");
  const [action, setAction] = useState<"approve" | "reject" | "hold">(
    "approve",
  );

  const handleSubmit = () => {
    switch (action) {
      case "approve":
        onApprove(notes);
        break;
      case "reject":
        onReject(notes);
        break;
      case "hold":
        onHold(notes);
        break;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold text-gray-900">
            Expense Approval
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Expense Summary */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">Expense Summary</h4>
            <div className="text-sm text-gray-600 space-y-1">
              <p>
                <strong>Amount:</strong> KSh {expense.amount.toLocaleString()}
              </p>
              <p>
                <strong>Category:</strong> {expense.category}
              </p>
              <p>
                <strong>Description:</strong> {expense.description}
              </p>
              <p>
                <strong>Date:</strong>{" "}
                {new Date(expense.date).toLocaleDateString()}
              </p>
            </div>
          </div>

          {/* Approval Action */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Action
            </label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="action"
                  value="approve"
                  checked={action === "approve"}
                  onChange={(e) => setAction(e.target.value as "approve")}
                  className="mr-2"
                />
                <CheckCircle className="w-4 h-4 text-green-600 mr-1" />
                Approve
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="action"
                  value="reject"
                  checked={action === "reject"}
                  onChange={(e) => setAction(e.target.value as "reject")}
                  className="mr-2"
                />
                <XCircle className="w-4 h-4 text-red-600 mr-1" />
                Reject
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="action"
                  value="hold"
                  checked={action === "hold"}
                  onChange={(e) => setAction(e.target.value as "hold")}
                  className="mr-2"
                />
                <Clock className="w-4 h-4 text-yellow-600 mr-1" />
                On Hold
              </label>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              placeholder="Add any notes about this approval decision..."
            />
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Submit
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminExpenses;
